import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { ArrowRight, FileText, FolderInput, Loader2, MessageSquare, Sparkles, Trash2, Eye, X, AlertTriangle } from 'lucide-react';
import { toast } from 'react-hot-toast';

import apiService from '../services/api';
import { useFileTreeContext } from '../context/FileTreeContext';
import type { AppShellOutletContext } from '../components/AppShell/AppShell';
import type { DocumentPreview } from '../types';

interface ProjectDoc {
  id: number;
  filename: string;
  status: string;
  page_count?: number | null;
  created_at?: string | null;
}

interface ProjectDetail {
  id: string;
  name: string;
  description?: string | null;
  document_count: number;
  documents: ProjectDoc[];
}

const STATUS_LABELS: Record<string, string> = {
  uploaded: 'הועלה',
  processing: 'מעבד OCR',
  ocr_processing: 'מעבד OCR',
  completed: 'מוכן',
  failed: 'נכשל',
  ready: 'מוכן',
};

const formatDate = (iso?: string | null): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const ProjectFilesPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { projects, refreshProjects } = useOutletContext<AppShellOutletContext>();
  const { clearFileSelection, toggleFileSelection } = useFileTreeContext();
  const [project, setProject] = useState<ProjectDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [reprocessingId, setReprocessingId] = useState<number | null>(null);
  // "מה חולץ" preview — lets the user verify what the agent actually reads.
  const [preview, setPreview] = useState<DocumentPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const handlePreview = async (docId: number) => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      setPreview(await apiService.getDocumentPreview(docId, 4000));
    } catch {
      toast.error('טעינת התצוגה נכשלה');
    } finally {
      setPreviewLoading(false);
    }
  };

  const loadProject = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = (await apiService.getProject(projectId)) as ProjectDetail;
      setProject(data);
    } catch {
      toast.error('טעינת קבצי הפרויקט נכשלה');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    let cancelled = false;
    if (!projectId) return;
    setLoading(true);
    apiService
      .getProject(projectId)
      .then((p) => {
        if (cancelled) return;
        setProject(p as ProjectDetail);
      })
      .catch(() => {
        if (cancelled) return;
        toast.error('טעינת קבצי הפרויקט נכשלה');
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  // Re-run OCR with MinerU — W3's GPU layout-aware engine that preserves tables
  // as HTML. The default "smart"/tesseract engine garbles Hebrew financial
  // tables (payment schedules etc.), so this fixes docs extracted poorly.
  const handleReprocessMineru = async (docId: number, filename: string) => {
    if (!window.confirm(`לעבד מחדש את "${filename}" עם MinerU (OCR איכותי לטבלאות)? זה ייקח מספר דקות.`)) {
      return;
    }
    setReprocessingId(docId);
    try {
      await apiService.reprocessDocument(docId, { force: true, force_ocr: true, engine: 'mineru' });
      toast.success(`"${filename}" נשלח לעיבוד מחדש עם MinerU — יתעדכן בעוד מספר דקות`);
      await loadProject();
    } catch {
      toast.error('עיבוד מחדש נכשל');
    } finally {
      setReprocessingId(null);
    }
  };

  const handleDelete = async (docId: number, filename: string) => {
    if (!window.confirm(`למחוק את "${filename}"? הפעולה אינה הפיכה.`)) return;
    setDeletingId(docId);
    try {
      await apiService.deleteDocument(docId);
      toast.success(`"${filename}" נמחק`);
      await loadProject();
      refreshProjects();
    } catch {
      toast.error('מחיקת המסמך נכשלה');
    } finally {
      setDeletingId(null);
    }
  };

  // Scope the chat to a single document (not the whole project). Selecting the
  // file wins over project scope in the chat's ScopeChip, so the next message
  // is answered about this document alone.
  const handleChatAboutDoc = (docId: number) => {
    clearFileSelection();
    toggleFileSelection(`doc-${docId}`);
    navigate(`/p/${projectId}/c/new`);
  };

  const handleMoveToProject = async (docId: number, targetProjectId: string, filename: string) => {
    if (!targetProjectId || targetProjectId === projectId) return;
    setMovingId(docId);
    try {
      await apiService.assignDocumentsToProject(targetProjectId, [docId]);
      const target = projects.find((p) => p.id === targetProjectId);
      toast.success(`"${filename}" הועבר ל"${target?.name || 'פרויקט'}"`);
      await loadProject();
      refreshProjects();
    } catch {
      toast.error('העברת המסמך נכשלה');
    } finally {
      setMovingId(null);
    }
  };

  const docs = project?.documents ?? [];
  const otherProjects = projects.filter((p) => p.id !== projectId);

  return (
    <div className="flex-1 overflow-auto bg-app" dir="rtl">
      <div className="max-w-5xl mx-auto px-6 py-8">
        <header className="flex items-start justify-between gap-4 mb-6">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <Link to="/p" className="hover:text-gray-700 transition-colors">
                פרויקטים
              </Link>
              <span aria-hidden="true">/</span>
              <span className="truncate">{project?.name || '...'}</span>
            </div>
            <h1 className="text-2xl font-semibold text-gray-900 truncate">
              {project?.name || 'טוען...'}
            </h1>
            {project?.description ? (
              <p className="text-sm text-gray-500 mt-1">{project.description}</p>
            ) : null}
            <p className="text-xs text-gray-500 mt-1">
              {docs.length} {docs.length === 1 ? 'מסמך' : 'מסמכים'} בפרויקט
            </p>
          </div>
          {projectId && (
            <Link
              to={`/p/${projectId}/c/new`}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-medium press flex-shrink-0"
              title="פתח שיחה על הפרויקט"
            >
              <MessageSquare className="h-4 w-4" />
              שיחה על הפרויקט
              <ArrowRight className="h-4 w-4 rotate-180" />
            </Link>
          )}
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : docs.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-10 text-center animate-fade-in motion-reduce:animate-none">
            <FileText className="h-10 w-10 text-gray-300 mx-auto mb-3" />
            <h2 className="text-base font-medium text-gray-700 mb-1">אין עדיין קבצים בפרויקט</h2>
            <p className="text-sm text-gray-500">
              צרף קבצים דרך הצ׳אט (גרור לחלון השיחה או לחץ ➕) ושייך אותם לפרויקט.
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <ul className="divide-y divide-gray-100">
              {docs.map((doc, i) => {
                const busy = deletingId === doc.id || movingId === doc.id || reprocessingId === doc.id;
                return (
                  <li
                    key={doc.id}
                    style={{ animationDelay: `${Math.min(i, 10) * 35}ms` }}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors animate-message-in motion-reduce:animate-none"
                  >
                    <div className="h-8 w-8 rounded-lg bg-gray-100 text-gray-500 flex items-center justify-center flex-shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-800 truncate">{doc.filename}</div>
                      <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                        <span>{STATUS_LABELS[doc.status] || doc.status}</span>
                        {doc.page_count ? <span>· {doc.page_count} עמ׳</span> : null}
                        {doc.created_at ? <span>· {formatDate(doc.created_at)}</span> : null}
                      </div>
                    </div>

                    {/* Preview what was actually extracted ("מה חולץ") */}
                    <button
                      onClick={() => handlePreview(doc.id)}
                      title="מה חולץ מהמסמך (מה שהסוכן רואה)"
                      aria-label={`הצג מה חולץ מ-${doc.filename}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 press-sm flex-shrink-0"
                    >
                      <Eye className="h-4 w-4" />
                    </button>

                    {/* Chat about this single document */}
                    <button
                      onClick={() => handleChatAboutDoc(doc.id)}
                      title="שיחה על מסמך זה"
                      aria-label={`שיחה על ${doc.filename}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 press-sm flex-shrink-0"
                    >
                      <MessageSquare className="h-4 w-4" />
                    </button>

                    {/* Re-OCR with MinerU (table-aware) */}
                    <button
                      onClick={() => handleReprocessMineru(doc.id, doc.filename)}
                      disabled={busy}
                      title="עבד מחדש עם MinerU (OCR איכותי לטבלאות)"
                      aria-label={`עבד מחדש את ${doc.filename} עם MinerU`}
                      className="p-2 rounded-lg text-gray-400 hover:text-amber-600 hover:bg-amber-50 press-sm disabled:active:scale-100 flex-shrink-0 disabled:opacity-50"
                    >
                      {reprocessingId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                    </button>

                    {/* Move to another project */}
                    {otherProjects.length > 0 && (
                      <div className="relative flex items-center flex-shrink-0">
                        {movingId === doc.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-gray-400 mx-2" />
                        ) : (
                          <>
                            <FolderInput className="h-4 w-4 text-gray-400 pointer-events-none absolute right-2" />
                            <select
                              value=""
                              disabled={busy}
                              onChange={(e) => handleMoveToProject(doc.id, e.target.value, doc.filename)}
                              title="העבר לפרויקט"
                              aria-label={`העבר את ${doc.filename} לפרויקט`}
                              className="appearance-none bg-transparent text-xs text-transparent hover:bg-gray-100 rounded-lg ps-7 pe-1 py-2 cursor-pointer focus:outline-none focus:ring-1 focus:ring-primary-300 disabled:opacity-50 w-9"
                            >
                              <option value="" disabled className="text-gray-700">
                                העבר לפרויקט…
                              </option>
                              {otherProjects.map((p) => (
                                <option key={p.id} value={p.id} className="text-gray-700">
                                  {p.name}
                                </option>
                              ))}
                            </select>
                          </>
                        )}
                      </div>
                    )}

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(doc.id, doc.filename)}
                      disabled={busy}
                      title="מחק מסמך"
                      aria-label={`מחק את ${doc.filename}`}
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 press-sm disabled:active:scale-100 disabled:opacity-50 flex-shrink-0"
                    >
                      {deletingId === doc.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>

      {/* "מה חולץ" preview modal — verify what the agent actually reads. */}
      {(previewLoading || preview) && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => { setPreview(null); setPreviewLoading(false); }}
          dir="rtl"
        >
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center gap-2 text-gray-800 min-w-0">
                <Eye className="h-4 w-4 text-gray-500 flex-shrink-0" />
                <span className="text-sm font-semibold truncate">מה חולץ מהמסמך {preview ? `— ${preview.filename}` : ''}</span>
              </div>
              <button
                onClick={() => { setPreview(null); setPreviewLoading(false); }}
                className="p-1 rounded text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                aria-label="סגור"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {previewLoading || !preview ? (
              <div className="flex items-center justify-center py-16 text-gray-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-auto px-4 py-3">
                {(() => {
                  const q = preview.ocr_quality_score;
                  const bad = preview.looks_unreliable || (typeof q === 'number' && q < 40);
                  const qColor = bad ? 'text-red-600' : (typeof q === 'number' && q < 60) ? 'text-amber-600' : 'text-emerald-600';
                  return (
                    <>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mb-3">
                        <span>מנוע: <span className="font-medium text-gray-800">{preview.ocr_engine}</span></span>
                        <span>איכות OCR: <span className={`font-semibold ${qColor}`}>{q == null ? '—' : `${q}%`}</span></span>
                        <span>{preview.page_count} עמ׳ · {preview.char_count.toLocaleString()} תווים · ~{preview.avg_chars_per_page} לעמ׳</span>
                      </div>
                      {bad && (
                        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3">
                          <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                          <span>החילוץ נראה לא אמין (טקסט דל/לא קריא). אל תסתמך על ניתוח של מסמך זה — הפעל <strong>עיבוד מחדש (MinerU)</strong> ונסה שוב.</span>
                        </div>
                      )}
                      <div className="text-[11px] text-gray-400 mb-1">תצוגה מקדימה של הטקסט שהסוכן קורא:</div>
                      <pre className="whitespace-pre-wrap break-words text-[13px] leading-relaxed text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-3 max-h-[45vh] overflow-auto" dir="auto">
{preview.preview || '(לא חולץ טקסט)'}
                      </pre>
                      {preview.preview_truncated && (
                        <div className="text-[11px] text-gray-400 mt-1">… (מקוצר)</div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectFilesPage;
